import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Upload, FileText, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import BlueCubeSpinner from "../components/BlueCubeSpinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navigation from "../components/Navigation";
import { toast } from "sonner";
import { documentSizeError, DOCUMENT_SIZE_LABEL, uploadDocumentChunks } from "../utils/documentUpload";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientUploadPage = ({ user }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    documentType: 'article',
    category: 'article',
    submissionType: 'load'
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileError, setFileError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    e.target.value = '';
    setFileError('');
    if (selectedFile) {
      const sizeError = documentSizeError(selectedFile);
      const extension = selectedFile.name.split('.').pop().toLowerCase();
      const error = sizeError || (!['pdf', 'docx', 'txt', 'pptx', 'ppt'].includes(extension)
        ? 'Please upload a PDF, DOCX, TXT, PPTX, or PPT file.' : '');
      if (error) {
        setFile(null);
        setFileError(error);
        toast.error(error);
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }
    
    if (!formData.title.trim()) {
      toast.error('Please enter a document title');
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    setUploadProgress(0);
    setFileError('');
    
    try {
      const token = localStorage.getItem('access_token');
      const result = await uploadDocumentChunks({ file, token,
        url: `${API}/client/upload-chunk`, onProgress: setUploadProgress,
        metadata: { document_type: formData.documentType, title: formData.title.trim(),
          category: formData.category, submission_type: formData.submissionType },
      });
      setUploadResult(result);
      toast.success(result.requires_payment
        ? 'Review & publishing selected. Payment required to complete submission.'
        : result.message || 'Document submitted successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message || error.message || 'Error uploading document';
      setFileError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handlePayment = async () => {
    if (!uploadResult?.submission_id) return;
    
    setProcessingPayment(true);
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/client/submission/${uploadResult.submission_id}/pay`, {
        submission_id: uploadResult.submission_id,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Error processing payment');
      setProcessingPayment(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white border border-slate-200 rounded-md p-8 text-center">
            <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Login Required</h2>
            <p className="text-slate-600 mb-4">Please login to upload documents.</p>
            <Button data-testid="client-upload-login" onClick={() => navigate('/login')} className="bg-orange-500 hover:bg-orange-600">
              Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Fraunces, serif' }}>
              Submit Your Document
            </h1>
            <p className="text-slate-600 mt-1 text-sm">Upload your article, paper, or book to The Archive.</p>
          </div>
          <button
            data-testid="client-upload-help"
            type="button"
            className="flex items-center text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-full px-2 py-1"
            onClick={() => alert('1) Make sure you are logged in and your name is set in your profile. 2) Choose the document type and title. 3) Upload a PDF, DOCX, TXT, PPTX, or PPT. 4) Articles can be submitted either as load-only (free) or with review & publishing (75$ fee); all submissions go through admin approval.')}
          >
            <span className="inline-block w-4 h-4 mr-1 rounded-full border border-slate-400 flex items-center justify-center text-[10px]">?</span>
            Help
          </button>
        </div>

        {/* Removed duplicate heading block */}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <h3 className="font-semibold text-blue-800 mb-2">Submission Guidelines</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Accepted formats: PDF, DOCX, TXT, PPTX, PPT</li>
            <li data-testid="client-upload-limits">• Maximum {DOCUMENT_SIZE_LABEL}</li>
            <li>• All submissions require admin approval</li>
          </ul>
        </div>

        {!uploadResult ? (
          <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-md p-6 space-y-6">
            {/* User Info */}
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-md">
              <p className="text-sm text-slate-600">
                Uploading as: <strong>{user?.name || user?.email}</strong>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                All submissions require admin approval when needed. Review & publishing is available for articles with a 75$ fee.
              </p>
              <p className="text-xs text-slate-500 mt-1">
                For paid documents, please make sure your account email is the one linked to your <strong>PayPal</strong> account.
                This is the email we will use to send your payments.
              </p>
              {user?.role === 'admin' && (
                <button
                  data-testid="client-upload-dashboard"
                  type="button"
                  onClick={() => navigate('/admin')}
                  className="ml-1 text-orange-600 hover:underline"
                >
                  Go to dashboard
                </button>
              )}
            </div>

            {/* Document Title */}
            <div>
              <Label htmlFor="title">Document Title *</Label>
              <Input
                id="title"
                data-testid="client-upload-title"
                disabled={uploading}
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Enter the title of your document"
                required
              />
            </div>

            {/* Category */}
            <div>
              <Label htmlFor="category">Category *</Label>
              <select
                id="category"
                data-testid="client-upload-category"
                disabled={uploading}
                value={formData.category}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    category: value,
                    documentType: value === 'article' ? 'article' : 'book',
                  });
                }}
                className="w-full h-10 px-3 py-2 mt-1 border border-slate-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="article">Article/paper</option>
                <option value="course">Course</option>
                <option value="report">Report</option>
                <option value="thesis">Thesis</option>
                <option value="book">Book</option>
                <option value="note">Note</option>
                <option value="preprint">Preprint</option>
              </select>
            </div>

            {/* Submission Type */}
            <div>
              <Label htmlFor="submissionType">Submission Type *</Label>
              <select
                id="submissionType"
                data-testid="client-upload-submission-type"
                disabled={uploading}
                value={formData.submissionType}
                onChange={(e) => setFormData({ ...formData, submissionType: e.target.value })}
                className="w-full h-10 px-3 py-2 mt-1 border border-slate-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="load">Load only (Free)</option>
                <option value="review">Submit for review & publishing (75$ fee for articles)</option>
              </select>
            </div>

            {/* File Upload */}
            <div>
              <Label>Upload Document *</Label>
              <div className="mt-2 border-2 border-dashed border-slate-300 rounded-md p-6 text-center hover:border-orange-400 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.docx,.txt,.pptx,.ppt"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  data-testid="client-upload-file-input"
                  disabled={uploading}
                />
                <label htmlFor="file-upload" className="cursor-pointer" data-testid="client-upload-choose-file">
                  <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                  {file ? (
                    <p className="text-sm text-slate-700 break-all" data-testid="client-upload-selected-file">{file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB</p>
                  ) : (
                    <>
                      <p className="text-sm text-slate-600">Click to upload your file</p>
                      <p className="text-xs text-slate-400">PDF, DOCX, TXT, PPTX, PPT · {DOCUMENT_SIZE_LABEL}</p>
                    </>
                  )}
                </label>
              </div>
              {fileError && <p className="mt-2 text-sm text-red-600" role="alert" data-testid="client-upload-error">{fileError}</p>}
              {uploading && (
                <div className="mt-4" data-testid="client-upload-progress" role="status" aria-live="polite">
                  <div className="flex justify-between text-sm text-slate-600 mb-1">
                    <span>{uploadProgress >= 99 ? 'Saving and processing…' : 'Uploading…'}</span>
                    <span data-testid="client-upload-percentage">{uploadProgress}%</span>
                  </div>
                  <progress data-testid="client-upload-progress-bar" max="100" value={uploadProgress} className="w-full h-2 accent-orange-500" aria-label="Document upload progress" />
                </div>
              )}
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              data-testid="client-upload-submit"
              disabled={uploading || !file}
              className="w-full bg-orange-500 hover:bg-orange-600 flex items-center justify-center"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {uploadProgress >= 99 ? 'Processing Document…' : 'Uploading Document…'}
                  <BlueCubeSpinner />
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Submit Document
                </>
              )}
            </Button>
          </form>
        ) : (
          /* Upload Result */
          <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="client-upload-result">
            <div className={`flex items-start gap-4 mb-6 p-4 rounded-md ${uploadResult.requires_payment ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'}`}>
              {uploadResult.requires_payment ? (
                <AlertCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
              ) : (
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <h3 className={`font-semibold ${uploadResult.requires_payment ? 'text-orange-800' : 'text-green-800'}`}>
                  {uploadResult.requires_payment ? 'Review & publishing - Payment Required' : 'Submission Received'}
                </h3>
                <p data-testid="client-upload-result-message" className={`text-sm ${uploadResult.requires_payment ? 'text-orange-700' : 'text-green-700'}`}>
                  {uploadResult.message}
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Document Type:</span>
                <span className="font-medium capitalize" data-testid="client-upload-result-type">{uploadResult.document_type}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Category:</span>
                <span className="font-medium capitalize" data-testid="client-upload-result-category">{uploadResult.category}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Submission Type:</span>
                <span className="font-medium capitalize" data-testid="client-upload-result-submission-type">{uploadResult.submission_type}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Word Count:</span>
                <span className="font-medium" data-testid="client-upload-result-word-count">{uploadResult.word_count.toLocaleString()}</span>
              </div>
              {uploadResult.requires_payment && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Submission Fee:</span>
                  <span className="font-semibold text-green-600" data-testid="client-upload-result-fee">${uploadResult.payment_amount.toFixed(2)}</span>
                </div>
              )}
            </div>

            {uploadResult.requires_payment ? (
              <Button 
                onClick={handlePayment}
                data-testid="client-upload-pay"
                disabled={processingPayment}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {processingPayment ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Redirecting to Payment...
                  </>
                ) : (
                  <>Pay ${uploadResult.payment_amount.toFixed(2)} to Complete Submission</>
                )}
              </Button>
            ) : (
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/my-submissions')}
                  data-testid="client-upload-view-submissions"
                  className="w-full bg-orange-500 hover:bg-orange-600"
                >
                  View My Submissions
                </Button>
                <Button 
                  data-testid="client-upload-another"
                  onClick={() => {
                    setUploadResult(null);
                    setFile(null);
                    setFileError('');
                    setUploadProgress(0);
                    setFormData({ title: '', documentType: 'article', category: 'article', submissionType: 'load' });
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Submit Another Document
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientUploadPage;
